/**
 * Lab member Net Exposure Details → getBetDetailsForWorkstation.
 *
 * Lab body: `user` = logged-in profile id, `playerId` = viewed member.
 * Strix SQL: when `playerId` is set, filter is `player.id = playerId`
 * (not `${accessLevel}.id = user`). Do **not** also send `userName` —
 * a wrong display name wipes every row while open exposure still shows.
 */

import { useQuery } from '@tanstack/react-query'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { useCmMe } from '@/api/hooks/useCmMe'
import { keys } from '@/api/hooks/shared/keys'
import {
  toWorkstationBet,
  type WorkstationBetRow,
} from '@/api/map/workstation'
import { levelToRole, ROLE_LABEL, type Role } from '@/features/hierarchy/hierarchy'
import {
  defaultBetDateRange,
  type WorkstationBet,
} from '@/features/net-exposure/workstation-fixture'

function labLevelLabel(role: Role | undefined): string {
  if (!role) return 'Company Master'
  return ROLE_LABEL[role]
}

export type PlayerMarketBetsQuery = {
  playerId: string
  marketId: string
  playerUsername?: string
}

/** Lab `getBetDetailsForWorkstationToDate` body for the member Details modal. */
export function playerMarketBetsRequest(
  query: PlayerMarketBetsQuery,
  callerId: string,
  callerRole: Role | undefined,
) {
  const range = defaultBetDateRange()
  return {
    user: callerId,
    playerId: query.playerId,
    marketId: query.marketId,
    accessLevel: labLevelLabel(callerRole),
    page: 1,
    maxRow: 100,
    fromDate: range.fromDate,
    toDate: range.toDate,
    currencyType: 'All' as const,
    // playerId already scopes SQL — skip userName (wrong label → empty modal).
  }
}

/** Peel raw or Standard `{ data: { result } }` envelopes. */
export function peelWorkstationBetRows(res: unknown): WorkstationBetRow[] {
  if (!res || typeof res !== 'object') return []
  const o = res as Record<string, unknown>
  if (Array.isArray(o.result)) return o.result as WorkstationBetRow[]
  const data = o.data
  if (data && typeof data === 'object') {
    const inner = data as Record<string, unknown>
    if (Array.isArray(inner.result)) return inner.result as WorkstationBetRow[]
  }
  if (Array.isArray(data)) return data as WorkstationBetRow[]
  return []
}

async function loadPlayerMarketBets(
  query: PlayerMarketBetsQuery,
  callerId: string,
  callerRole: Role | undefined,
): Promise<WorkstationBet[]> {
  const res = await cmApi.getBetDetailsForWorkstation(
    authHeaders(),
    playerMarketBetsRequest(query, callerId, callerRole),
  )
  return peelWorkstationBetRows(res).map(toWorkstationBet)
}

function useCallerHierarchyId() {
  return useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
    staleTime: 60_000,
  })
}

export function usePlayerMarketBets(
  query: PlayerMarketBetsQuery | null,
  enabled: boolean,
) {
  const me = useCmMe()
  const self = useCallerHierarchyId()
  const callerId =
    me.data?.caller.agentId?.trim() || self.data?.node.id?.trim() || undefined
  const level = Number(me.data?.caller.hierarchyLevel)
  const callerRole = Number.isFinite(level) ? levelToRole(level) : 'company-master'

  return useQuery({
    queryKey: keys.playerMarketBets(query ?? { playerId: '', marketId: '' }),
    queryFn: () => loadPlayerMarketBets(query!, callerId!, callerRole),
    enabled: Boolean(
      enabled &&
        query?.playerId &&
        query.marketId &&
        callerId &&
        (me.isSuccess || self.isSuccess),
    ),
  })
}
